import { Flex, FlexColumn, Media, Tooltip } from "@opensea/ui-kit"
import { Add, ArrowDownward, Send, SwapCalls } from "@opensea/ui-kit/icons"
import { useTranslations } from "use-intl"
import { useSnapshot } from "valtio"
import { Button } from "../../../../../components/Button"
import { useIsAbstractWalletAccount } from "../../../../../hooks/useIsAbstractWalletAccount"
import { useChainArch } from "../../../../../polychain/hooks"
import { useFlag } from "../../../../../providers/FlagProvider/useFlag"
import { FlowActions, FlowState } from "../../../../../stores/flowStore"
import type { FlowStateVariants } from "../../../../flow-state"
import { useDepositFlow } from "../../../../hooks/useDepositFlow"
import { useSendFlow } from "../../../../hooks/useSendFlow"
import { useSwapFlow } from "../../../../hooks/useSwapFlow"
import { useTopupFlow } from "../../../../hooks/useTopupFlow"

type ActionButtonProps = {
  title: string
  icon: React.ReactNode
  onClick?: () => void
  disabled?: boolean
}

export function CryptoTabActions() {
  const t = useTranslations("wallet.CryptoTabActions")
  const { flow } = useSnapshot(FlowState)
  const sendFlow = useSendFlow()
  const swapFlow = useSwapFlow()
  const depositFlow = useDepositFlow()
  const topupFlow = useTopupFlow()
  const isAbstractWallet = useIsAbstractWalletAccount()

  const onBack = () => {
    if (flow) {
      FlowActions.startFlow(flow as FlowStateVariants)
    }
  }

  const chainArch = useChainArch()
  const solanaTokenSendEnabled = useFlag("solanaTokenSendEnabled")

  const isSendEnabled =
    chainArch === "EVM" || (chainArch === "SVM" && solanaTokenSendEnabled)

  return (
    <Flex className="justify-between gap-3">
      <ActionButton
        icon={<Send />}
        onClick={
          isSendEnabled
            ? () =>
                sendFlow({
                  onBack,
                })
            : undefined
        }
        title={t("send")}
      />
      <ActionButton
        icon={<SwapCalls />}
        onClick={() => swapFlow({ onBack })}
        title={t("swap")}
      />
      <ActionButton
        icon={<ArrowDownward />}
        onClick={() => depositFlow({ onBack })}
        title={t("deposit")}
      />
      <ActionButton
        disabled={isAbstractWallet}
        icon={<Add />}
        onClick={isAbstractWallet ? undefined : () => topupFlow({ onBack })}
        title={t("buy")}
      />
    </Flex>
  )
}

function ActionButton({ title, icon, onClick, disabled }: ActionButtonProps) {
  const t = useTranslations("wallet.CryptoTabActions")
  const isAbstractWallet = useIsAbstractWalletAccount()

  const getTooltipContent = () => {
    if (disabled && isAbstractWallet && title === t("buy")) {
      return t("abstractWalletBuyNotAllowed")
    }
    return t("comingSoon")
  }

  return (
    <Tooltip content={getTooltipContent()} disabled={Boolean(onClick)}>
      <div>
        <Media lessThan="lg">
          <Button
            className="h-20 w-[80px] flex-1 basis-[100px] justify-center rounded-xl p-3 sm:w-[calc(23vw-8px)]"
            disabled={!onClick || disabled}
            onClick={onClick}
            variant="secondary"
          >
            <FlexColumn className="gap-3">{icon}</FlexColumn>
          </Button>
          <Flex className="justify-center pt-2 text-sm">{title}</Flex>
        </Media>
        <Media greaterThanOrEqual="lg">
          <Button
            className="h-20 w-[94px] flex-1 basis-[100px] justify-start rounded-lg p-3"
            disabled={!onClick || disabled}
            onClick={onClick}
            variant="secondary"
          >
            <FlexColumn className="gap-3">
              <Flex>{icon}</Flex>
              <Flex>{title}</Flex>
            </FlexColumn>
          </Button>
        </Media>
      </div>
    </Tooltip>
  )
}
